import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledCard,
  VendorCardLabel,
} from "../../vendorRegistration/components/StepMain";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  Select,
  FormHelperText,
} from "@mui/material";
import { Super } from "../../vendorRegistration/components/steps/cardTrial";
import { history } from "../../../app/history";
import { addProject, getCompanyBusinessUnits } from "../redux/projectSlice";
import { getAssignCompanyList } from "../../businessUnit/redux/businessUnitSlice";

const PROJECT_STATUS_OPTIONS = [
  { value: "PLANNING", label: "Planning" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "ON_HOLD", label: "On Hold" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function ProjectContent() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { assignCompanyList } = useSelector((state) => state.businessUnit);
  const { companyBusinessUnits } = useSelector((state) => state.project);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const watchedCompanyId = watch("company_id");

  useEffect(() => {
    // Fetch companies for dropdown
    dispatch(getAssignCompanyList());
  }, [dispatch]);

  useEffect(() => {
    if (watchedCompanyId) {
      dispatch(getCompanyBusinessUnits(watchedCompanyId));
      // Optional: Clear business unit selection when company changes
      // setValue("business_unit_id", "");
    }
  }, [dispatch, watchedCompanyId]);

  const handleSave = (data) => {
    setLoading(true);
    const payload = {
      project_name: data.project_name,
      project_code: data.project_code,
      project_status: data.project_status,
      planned_company_start_date: data.planned_company_start_date || null,
      planned_company_end_date: data.planned_company_end_date || null,
      actual_company_start_date: data.actual_company_start_date || null,
      actual_company_end_date: data.actual_company_end_date || null,
      extended_company_end_date: data.extended_company_end_date || null,
      storeAddress: data.storeAddress,
      storeLocation: data.storeLocation,
      business_unit_id: data.business_unit_id,
      company_id: data.company_id,
      project_budget: data.project_budget,
    };

    dispatch(addProject(payload)).then((response) => {
      if (response?.payload?.success) {
        setLoading(false);
        history.push("/app/project");
      } else {
        setLoading(false);
      }
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleSave)}>
        <StyledCard>
          <CardContent>
            <Grid container spacing={3}>
              {/* Project Name */}
              <Grid item md={6} xs={12}>
                <VendorCardLabel>
                  Project Name<Super>*</Super>
                </VendorCardLabel>
                <TextField
                  sx={{
                    input: {
                      fontSize: "14px",
                      fontFamily: "var(--font-family-reg)",
                    },
                  }}
                  placeholder="Enter Project Name"
                  size="small"
                  variant="standard"
                  style={{ width: "100%", paddingTop: "3px" }}
                  {...register("project_name", {
                    required: "Please enter project name",
                  })}
                  error={Boolean(errors?.project_name)}
                  helperText={errors?.project_name?.message}
                />
              </Grid>

              {/* Project Code */}
              <Grid item md={6} xs={12}>
                <VendorCardLabel>
                  Project Code<Super>*</Super>
                </VendorCardLabel>
                <TextField
                  sx={{
                    input: {
                      fontSize: "14px",
                      fontFamily: "var(--font-family-reg)",
                    },
                  }}
                  placeholder="Enter Project Code"
                  size="small"
                  variant="standard"
                  style={{ width: "100%", paddingTop: "3px" }}
                  {...register("project_code", {
                    required: "Please enter project code",
                  })}
                  error={Boolean(errors?.project_code)}
                  helperText={errors?.project_code?.message}
                />
              </Grid>

              {/* Project Status */}
              <Grid item md={6} xs={12}>
                <VendorCardLabel>
                  Project Status<Super>*</Super>
                </VendorCardLabel>
                <FormControl
                  variant="standard"
                  fullWidth
                  error={Boolean(errors?.project_status)}
                >
                  <Controller
                    name="project_status"
                    control={control}
                    rules={{ required: "Please select project status" }}
                    render={({ field }) => (
                      <Select {...field} displayEmpty>
                        <MenuItem value="" disabled>
                          Select Status
                        </MenuItem>
                        {PROJECT_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors?.project_status?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {/* Company */}
              <Grid item md={6} xs={12}>
                <VendorCardLabel>
                  Company<Super>*</Super>
                </VendorCardLabel>
                <FormControl
                  variant="standard"
                  fullWidth
                  error={Boolean(errors?.company_id)}
                >
                  <Controller
                    name="company_id"
                    control={control}
                    rules={{ required: "Please select company" }}
                    render={({ field }) => (
                      <Select {...field} displayEmpty>
                        <MenuItem value="" disabled>
                          Select Company
                        </MenuItem>
                        {assignCompanyList?.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            {company.company_name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors?.company_id?.message}</FormHelperText>
                </FormControl>
              </Grid>
              {/* Business Unit */}
              <Grid item md={6} xs={12}>
                <VendorCardLabel>
                  Business Unit<Super>*</Super>
                </VendorCardLabel>
                <FormControl
                  variant="standard"
                  fullWidth
                  error={Boolean(errors?.business_unit_id)}
                >
                  <Controller
                    name="business_unit_id"
                    control={control}
                    rules={{ required: "Please select business unit" }}
                    render={({ field }) => (
                      <Select {...field} displayEmpty>
                        <MenuItem value="" disabled>
                          Select Business Unit
                        </MenuItem>
                        {companyBusinessUnits?.map((bu) => (
                          <MenuItem
                            key={bu.companyBusinessUnitId}
                            value={bu.businessUnitId}
                          >
                            {bu.businessUnitName}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors?.business_unit_id?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Project Budget */}
              <Grid item md={6} xs={12}>
                <VendorCardLabel>Project Budget</VendorCardLabel>
                <TextField
                  sx={{
                    input: {
                      fontSize: "14px",
                      fontFamily: "var(--font-family-reg)",
                    },
                  }}
                  type="number"
                  placeholder="Enter Project Budget"
                  size="small"
                  variant="standard"
                  style={{ width: "100%", paddingTop: "3px" }}
                  {...register("project_budget")}
                />
              </Grid>

              {/* Planned Company Start Date */}
              <Grid item md={6} xs={12}>
                <FormControl variant="standard" style={{ width: "100%" }}>
                  <VendorCardLabel>Planned Company Start Date</VendorCardLabel>
                  <TextField
                    sx={{
                      input: {
                        fontSize: "14px",
                        fontFamily: "var(--font-family-reg)",
                      },
                    }}
                    InputProps={{
                      disableUnderline: false,
                      disableInjectingGlobalStyles: true,
                    }}
                    InputLabelProps={{
                      disableAnimation: true,
                      disableInjectingGlobalStyles: true,
                    }}
                    size="small"
                    variant="standard"
                    type="date"
                    style={{
                      width: "100%",
                      paddingTop: "3px",
                    }}
                    {...register("planned_company_start_date")}
                  />
                </FormControl>
              </Grid>

              {/* Planned Company End Date */}
              <Grid item md={6} xs={12}>
                <FormControl variant="standard" style={{ width: "100%" }}>
                  <VendorCardLabel>Planned Company End Date</VendorCardLabel>
                  <TextField
                    sx={{
                      input: {
                        fontSize: "14px",
                        fontFamily: "var(--font-family-reg)",
                      },
                    }}
                    InputProps={{
                      disableUnderline: false,
                      disableInjectingGlobalStyles: true,
                    }}
                    InputLabelProps={{
                      disableAnimation: true,
                      disableInjectingGlobalStyles: true,
                    }}
                    size="small"
                    variant="standard"
                    type="date"
                    style={{
                      width: "100%",
                      paddingTop: "3px",
                    }}
                    {...register("planned_company_end_date")}
                  />
                </FormControl>
              </Grid>

              {/* Actual Company Start Date */}
              <Grid item md={6} xs={12}>
                <FormControl variant="standard" style={{ width: "100%" }}>
                  <VendorCardLabel>Actual Company Start Date</VendorCardLabel>
                  <TextField
                    sx={{
                      input: {
                        fontSize: "14px",
                        fontFamily: "var(--font-family-reg)",
                      },
                    }}
                    InputProps={{
                      disableUnderline: false,
                      disableInjectingGlobalStyles: true,
                    }}
                    InputLabelProps={{
                      disableAnimation: true,
                      disableInjectingGlobalStyles: true,
                    }}
                    size="small"
                    variant="standard"
                    type="date"
                    style={{
                      width: "100%",
                      paddingTop: "3px",
                    }}
                    {...register("actual_company_start_date")}
                  />
                </FormControl>
              </Grid>

              {/* Actual Company End Date */}
              <Grid item md={6} xs={12}>
                <FormControl variant="standard" style={{ width: "100%" }}>
                  <VendorCardLabel>Actual Company End Date</VendorCardLabel>
                  <TextField
                    sx={{
                      input: {
                        fontSize: "14px",
                        fontFamily: "var(--font-family-reg)",
                      },
                    }}
                    InputProps={{
                      disableUnderline: false,
                      disableInjectingGlobalStyles: true,
                    }}
                    InputLabelProps={{
                      disableAnimation: true,
                      disableInjectingGlobalStyles: true,
                    }}
                    size="small"
                    variant="standard"
                    type="date"
                    style={{
                      width: "100%",
                      paddingTop: "3px",
                    }}
                    {...register("actual_company_end_date")}
                  />
                </FormControl>
              </Grid>

              {/* Extended Company End Date */}
              <Grid item md={6} xs={12}>
                <FormControl variant="standard" style={{ width: "100%" }}>
                  <VendorCardLabel>Extended Company End Date</VendorCardLabel>
                  <TextField
                    sx={{
                      input: {
                        fontSize: "14px",
                        fontFamily: "var(--font-family-reg)",
                      },
                    }}
                    InputProps={{
                      disableUnderline: false,
                      disableInjectingGlobalStyles: true,
                    }}
                    InputLabelProps={{
                      disableAnimation: true,
                      disableInjectingGlobalStyles: true,
                    }}
                    size="small"
                    variant="standard"
                    type="date"
                    style={{
                      width: "100%",
                      paddingTop: "3px",
                    }}
                    {...register("extended_company_end_date")}
                  />
                </FormControl>
              </Grid>

              {/* Store Address */}
              <Grid item md={12} xs={12}>
                <VendorCardLabel>Store Address</VendorCardLabel>
                <TextField
                  multiline
                  rows={1}
                  placeholder="Enter Store Address"
                  variant="standard"
                  style={{ width: "100%", paddingTop: "3px" }}
                  {...register("storeAddress")}
                />
              </Grid>

              {/* Store Location */}
              <Grid item md={12} xs={12}>
                <VendorCardLabel>Store Location</VendorCardLabel>
                <TextField
                  multiline
                  rows={1}
                  placeholder="Enter Store Location"
                  variant="standard"
                  style={{ width: "100%", paddingTop: "3px" }}
                  {...register("storeLocation")}
                />
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            pt: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flex: "1 1 auto" }} />
          <LoadingButton
            sx={{
              textTransform: "none",
              background: "#4059e5",
              width: "200px",
              height: "40px",
            }}
            color="primary"
            variant="contained"
            type="submit"
            loading={loading}
            loadingPosition="start"
          >
            Create
          </LoadingButton>
        </Box>
      </form>
    </>
  );
}

export default ProjectContent;
